/**
 * ═══════════════════════════════════════════════════════════════════
 *  TourProvider — React context provider managing tour lifecycle
 * ═══════════════════════════════════════════════════════════════════
 *
 *  Usage:
 *    <TourProvider
 *      tourId="prescription-practice"
 *      steps={mySteps}
 *      autoStart={true}
 *      onComplete={(tourId) => markTourDone(tourId)}
 *      onSkip={(tourId, stepIdx) => console.log('skipped')}
 *    >
 *      <MyPageContent />
 *    </TourProvider>
 *
 *  Then inside any child:
 *    const { startTour, nextStep, isActive, currentStep } = useTour();
 *
 *  No auth, no store, no API imports — pure UI state management.
 */

import React, { useReducer, useCallback, useMemo, useEffect } from 'react';
import TourContext from './TourContext';
import TourOverlay from './TourOverlay';
import { tourReducer, createInitialState, TOUR_ACTIONS, TOUR_STATUS } from './tourReducer';

export default function TourProvider({
  children,
  tourId = 'default',
  steps = [],
  autoStart = false,
  onComplete,
  onSkip,
  overlayColor = 'rgba(0, 0, 0, 0.55)',
  isDark = false,
}) {
  const [state, dispatch] = useReducer(tourReducer, steps, createInitialState);

  // ── Actions ───────────────────────────────────────────────────────
  const startTour = useCallback((overrideSteps) => {
    dispatch({ type: TOUR_ACTIONS.START, steps: overrideSteps || undefined });
  }, []);

  const nextStep = useCallback(() => {
    if (state.stepIndex >= state.totalSteps - 1) {
      // Last step → complete
      dispatch({ type: TOUR_ACTIONS.COMPLETE });
      if (onComplete) onComplete(tourId);
    } else {
      dispatch({ type: TOUR_ACTIONS.NEXT });
    }
  }, [state.stepIndex, state.totalSteps, onComplete, tourId]);

  const prevStep = useCallback(() => {
    dispatch({ type: TOUR_ACTIONS.PREV });
  }, []);

  const skipTour = useCallback(() => {
    dispatch({ type: TOUR_ACTIONS.SKIP });
    if (onSkip) onSkip(tourId, state.stepIndex);
  }, [onSkip, tourId, state.stepIndex]);

  const completeTour = useCallback(() => {
    dispatch({ type: TOUR_ACTIONS.COMPLETE });
    if (onComplete) onComplete(tourId);
  }, [onComplete, tourId]);

  const goToStep = useCallback((idx) => {
    dispatch({ type: TOUR_ACTIONS.GO_TO, stepIndex: idx });
  }, []);

  const pauseTour = useCallback(() => {
    dispatch({ type: TOUR_ACTIONS.PAUSE });
  }, []);

  const resumeTour = useCallback(() => {
    dispatch({ type: TOUR_ACTIONS.RESUME });
  }, []);

  const resetTour = useCallback(() => {
    dispatch({ type: TOUR_ACTIONS.RESET });
  }, []);

  // ── Auto-start ────────────────────────────────────────────────────
  useEffect(() => {
    if (autoStart && steps.length > 0 && state.status === TOUR_STATUS.IDLE) {
      // Small delay to let the page render so target elements exist
      const timer = setTimeout(() => startTour(), 500);
      return () => clearTimeout(timer);
    }
  }, [autoStart, steps.length, state.status, startTour]);

  // ── Update steps if they change externally ────────────────────────
  useEffect(() => {
    if (state.status === TOUR_STATUS.IDLE && steps !== state.steps) {
      dispatch({ type: TOUR_ACTIONS.RESET });
    }
  }, [steps]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Context value ─────────────────────────────────────────────────
  const currentStep = state.steps[state.stepIndex] || null;
  const isActive = state.status === TOUR_STATUS.RUNNING;

  const value = useMemo(() => ({
    startTour,
    nextStep,
    prevStep,
    skipTour,
    completeTour,
    goToStep,
    goTo: goToStep,     // Alias for convenience
    pauseTour,
    resumeTour,
    resetTour,
    currentStep,
    stepIndex: state.stepIndex,
    totalSteps: state.totalSteps,
    isActive,
    status: state.status,
    tourId,
  }), [
    startTour, nextStep, prevStep, skipTour, completeTour,
    goToStep, pauseTour, resumeTour, resetTour,
    currentStep, state.stepIndex, state.totalSteps, isActive, state.status, tourId,
  ]);

  return (
    <TourContext.Provider value={value}>
      {children}
      <TourOverlay
        status={state.status}
        step={currentStep}
        stepIndex={state.stepIndex}
        totalSteps={state.totalSteps}
        onNext={nextStep}
        onPrev={prevStep}
        onSkip={skipTour}
        overlayColor={overlayColor}
        isDark={isDark}
      />
    </TourContext.Provider>
  );
}
